import { Dispatch, AppThunk, RequestStatusName, RootState } from '../storeTypes';
import {
  assignTreatmentPlan,
  createTreatmentPlanTemplate,
  deleteTreatmentPlan,
  deleteTreatmentPlanTemplate,
  editTreatmentPlanTemplate,
  fetchMovementsResults,
  fetchTreatmentPlanTemplates,
  fetchTreatmentPlanById,
  fetchUserTreatmentPlans,
  updateAssignedTreatmentPlan,
} from '../../services/treatmentPlanService';
import {
  LIVE_EDIT_SENDS_SLOT_IDENTITY,
  toTreatmentPlanInputDto,
  toTreatmentPlanTemplateInputDto,
} from '../../types/mappers/treatmentPlanMapper';
import {
  addLoadedResultsRangeAction,
  setMovementsResultsAction,
  setTreatmentPlansAction,
} from '../slices/treatmentPlansSlice';
import {
  getCurrentPatientIdSelector,
  getRequestStatusSelector,
  getSelectedCaseIdSelector,
  getUserIdSelector,
} from '../selectors/appSelectors';
import { isInProcess, skipRequest } from '../utils/requestStatus';
import { coversRange } from '../utils/resultsCoverage';
import { cleanRequestStatusAction, markStatusFunctionCreator } from '../slices/appSlice';
import { trackPortalEvent } from '../../observability/rum/rumClient';
import { PORTAL_EVENTS } from '../../observability/rum/rumEvents';
import {
  WORKFLOWS,
  startWorkflowTimer,
  completeWorkflowTimer,
} from '../../observability/rum/workflowTimer';
import {
  MovementResultsDto,
  MovementResultsOutputDto,
  TreatmentPlanDto,
  TreatmentPlanInputDto,
  TreatmentPlanTemplateDto,
  TreatmentPlanTemplateInputDto,
} from '../../types/backendType';
import {
  ActivityInput,
  TreatmentPlanInput,
} from '../../components/patient-details/treatment-plans/creation/treatmentPlanTypes';
import { notify } from '../slices/notificationsSlice';
import {
  addTreatmentPlanTemplateAction,
  deleteTreatmentPlanTemplateFromStoreAction,
  setTreatmentPlanTemplatesAction,
} from '../slices/librarySlice';
import { getActivityDefinitionsSelector } from '../selectors/librarySelectors';
import {
  getAllActivityDefinitionsSelector,
  getCustomActivityDefinitionsByMovementIdSelector,
} from '../selectors/customActivitiesSelectors';
import {
  END_DATE_FOR_REQUESTS,
  START_DATE_FOR_REQUESTS,
  resultsWindowStart,
} from '../../config';
import { AskUser } from '../../components/commons/AskUser';
import { TreatmentPlan } from '../../models/TreatmentPlan';
import { UserId } from '../../models/User';
import { TreatmentPlanTemplate } from '../../models/TreatmentPlanTemplate';
import { getPatientTreatmentPlansSelector } from '../../models/factories/treatmentPlanFactories';
import { getPatientActivitiesExecutionsSelector } from '../../models/ActivityExecution';
import { getPatientCaseDtosSelector } from '../selectors/casesSelectors';
import { mayDeleteLatestPlanBeforeAssign } from '../../components/patient-details/treatment-plans/creation/livePlanSave';
import {
  activitiesOfPlan,
  mergePatientDeferrals,
  movedExerciseNames,
} from '../../components/patient-details/treatment-plans/creation/patientDeferralMerge';
import {
  LEGACY_CASE_ID,
  sameCase,
} from '../../components/patient-details/treatment-plans/creation/mergedCases';

export function fetchAssignTreatmentPlansAction({
  patientId,
}: {
  patientId: string;
}): AppThunk {
  return async (dispatch) => {
    const markStatus = markStatusFunctionCreator(
      dispatch,
      patientId,
      'fetchAssignTreatmentPlans',
    );
    markStatus('inProcess');
    let treatmentPlansDto: TreatmentPlanDto[] | undefined;
    try {
      treatmentPlansDto = await fetchUserTreatmentPlans({
        userId: patientId,
        startDate: START_DATE_FOR_REQUESTS,
        endDate: END_DATE_FOR_REQUESTS,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      return;
    }
    markStatus('succeeded');
    if (!treatmentPlansDto) {
      return;
    }
    dispatch(setTreatmentPlansAction({ treatmentPlansDto, patientId }));
  };
}

async function fetchMovementsResultsRoutine({
  dispatch,
  getState,
  patientId,
  startDate,
  endDate,
  force,
}: {
  dispatch: Dispatch;
  getState: () => RootState;
  patientId: string;
  startDate?: string;
  endDate?: string;
  force?: boolean;
}) {
  const markStatus = markStatusFunctionCreator(
    dispatch,
    patientId,
    'fetchActivitiesResultsByUserId',
  );
  const requestStatus = getRequestStatusSelector('fetchActivitiesResultsByUserId')(getState());
  // `force` is what the "Load earlier sessions" control uses. Without it the
  // widen would be a no-op: skipRequest never expires, so once the default
  // 180-day load has succeeded for a patient, every later call for the SAME
  // patient is dropped — including one asking for a wider window.
  if (!force && skipRequest({ requestStatus, id: patientId })) {
    return;
  }
  markStatus('inProcess');
  const effectiveStart = startDate ? new Date(startDate) : resultsWindowStart();
  let movementResultsOutputDto: MovementResultsOutputDto | undefined;
  try {
    movementResultsOutputDto = await fetchMovementsResults({
      userId: patientId,
      // 180 days by default, not ten years. The patient page draws one day; the
      // rest is fetched only when the clinician asks for it via
      // loadResultsRangeAction. See RESULTS_WINDOW_DAYS for why 180.
      startDate: effectiveStart,
      ...(endDate ? { endDate: new Date(endDate) } : {}),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    markStatus('failed');
    throw e;
  }
  markStatus('succeeded');
  if (!movementResultsOutputDto) {
    return;
  }
  dispatch(
    setMovementsResultsAction({
      patientId,
      movementsResults: movementResultsOutputDto.movements,
    }),
  );
  // Record EXACTLY what this call covered, next to the rows it produced, so
  // coverage cannot drift from what is actually in the store. Doing it here
  // rather than at each call site also means the default 180-day load is
  // recorded, which is what lets the Plans gate reason about it at all.
  dispatch(
    addLoadedResultsRangeAction({
      patientId,
      from: effectiveStart.toISOString(),
      to: (endDate ? new Date(endDate) : new Date()).toISOString(),
    }),
  );
  return movementResultsOutputDto;
}

export function fulfillAllActivitiesResultsAction(
  patientId: UserId,
  startDate?: string,
): AppThunk {
  return async (dispatch, getState) => {
    try {
      fetchMovementsResultsRoutine({ dispatch, getState, patientId, startDate });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };
}

/**
 * Load a specific stretch of history, on request.
 *
 * The default load stops at RESULTS_WINDOW_DAYS. This is the other half of that
 * decision: without it the cap would make older sessions UNREACHABLE rather
 * than on-demand, because the Sessions day list is built purely from the rows
 * that were fetched — a day that was not loaded cannot be picked, because it is
 * not in the list.
 *
 * Takes an explicit `from`/`to` so a clinician can ask for a period that ENDS
 * IN THE PAST ("a year ending June 2025"), not only "everything since some
 * date". That is what makes coverage disjoint, and why it is tracked as ranges
 * — see utils/resultsCoverage.ts.
 *
 * Additive: setMovementsResultsAction de-duplicates on (session_id,
 * sub_session_id), so a new range folds into what is already held rather than
 * replacing it. Asking for a range already covered is a no-op — those rows are
 * in the store, so the request would cost a round trip and return nothing new.
 */
export function loadResultsRangeAction(
  patientId: UserId,
  from: string,
  to: string,
): AppThunk<Promise<number | null>> {
  // Resolves to how many movement results the load ADDED (0 when the range was
  // already held, or held nothing new), or null when it failed. The Sessions
  // tab uses 0 to say "No earlier sessions" instead of closing silently.
  return async (dispatch, getState) => {
    const held = getState().treatmentPlansState.loadedResultsRanges[patientId] ?? [];
    if (coversRange(held, { from, to })) {
      return 0;
    }
    const before = getState().treatmentPlansState.movementsResults[patientId] ?? [];
    const isHeld = (row: MovementResultsDto) =>
      before.some(
        ({ session_id, sub_session_id }) =>
          session_id === row.session_id && sub_session_id === row.sub_session_id,
      );
    try {
      // The routine records the covered range itself, on success only:
      // recording a failed load would claim history we do not have, and the
      // Plans gate would then read an empty result set as "never executed".
      const output = await fetchMovementsResultsRoutine({
        dispatch,
        getState,
        patientId,
        startDate: from,
        endDate: to,
        force: true,
      });
      return (output?.movements ?? []).filter((row) => !isHeld(row)).length;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return null;
    }
  };
}

async function createTreatmentPlanTemplateRoutine({
  dispatch,
  treatmentPlanForm,
  getState,
  edit,
}: {
  dispatch: Dispatch;
  treatmentPlanForm: TreatmentPlanInput;
  getState: () => RootState;
  edit?: boolean;
}) {
  const id = treatmentPlanForm.id;
  const requestStatusName: RequestStatusName = edit
    ? 'editTreatmentPlanTemplate'
    : 'createTreatmentPlanTemplate';
  const requestStatus = getRequestStatusSelector(requestStatusName)(getState());
  if (edit) {
    cleanRequestStatusAction({ requestStatusName, id });
  }
  if (!edit && skipRequest({ requestStatus, id })) {
    return;
  }
  const markStatus = markStatusFunctionCreator(dispatch, id, requestStatusName);
  markStatus('inProcess');
  let treatmentPlanTemplateDto: TreatmentPlanTemplateDto | undefined = undefined;
  try {
    // Merge custom-activity versions into the definitions map so toMovementInput resolves
    // custom ids (it throws on any id absent from this map).
    const activityDefinitions = {
      ...getActivityDefinitionsSelector(getState()),
      ...getCustomActivityDefinitionsByMovementIdSelector(getState()),
    };
    const treatmentPlanTemplateInputDto: TreatmentPlanTemplateInputDto =
      toTreatmentPlanTemplateInputDto(treatmentPlanForm, activityDefinitions);
    if (edit) {
      treatmentPlanTemplateDto = await editTreatmentPlanTemplate(
        treatmentPlanForm.id,
        treatmentPlanTemplateInputDto,
      );
    } else {
      treatmentPlanTemplateDto = await createTreatmentPlanTemplate({
        treatmentPlanTemplateInputDto,
      });
    }

    if (!treatmentPlanTemplateDto) {
      throw new Error();
    }
    const treatmentPlanTemplatesDto = await fetchTreatmentPlanTemplates();
    if (treatmentPlanTemplatesDto) {
      dispatch(setTreatmentPlanTemplatesAction({ treatmentPlanTemplatesDto }));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    markStatus('failed');
    throw e;
  }
  markStatus('succeeded');
}

export function createTreatmentPlanTemplateAction(
  treatmentPlanForm: TreatmentPlanInput,
  edit?: boolean,
): AppThunk {
  return async (dispatch, getState) => {
    try {
      await createTreatmentPlanTemplateRoutine({
        dispatch,
        getState,
        treatmentPlanForm,
        edit,
      });
    } catch (e) {
      dispatch(
        notify({
          message: 'failed to create treatment plan template',
          severity: 'error',
        }),
      );
      return;
    }
    dispatch(
      notify({
        message: 'the treatment plan template was created successfully',
        severity: 'success',
      }),
    );
  };
}

export function fetchTreatmentPlanTemplatesAction(): AppThunk {
  return async (dispatch) => {
    const markStatus = markStatusFunctionCreator(
      dispatch,
      'fetchTreatmentPlanTemplates',
      'fetchTreatmentPlanTemplates',
    );
    markStatus('inProcess');
    let treatmentPlanTemplatesDto: TreatmentPlanTemplateDto[] | undefined;
    try {
      treatmentPlanTemplatesDto = await fetchTreatmentPlanTemplates();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      return;
    }
    markStatus('succeeded');
    if (!treatmentPlanTemplatesDto) {
      return;
    }
    dispatch(setTreatmentPlanTemplatesAction({ treatmentPlanTemplatesDto }));
  };
}

async function deleteTreatmentPlanRoutine({
  dispatch,
  treatmentPlanId,
  getState,
  patientId,
}: {
  dispatch: Dispatch;
  treatmentPlanId: string;
  getState: () => RootState;
  patientId: string;
}) {
  const requestStatusId = treatmentPlanId;
  const requestStatusName: RequestStatusName = 'deleteTreatmentPlan';
  const requestStatus = getRequestStatusSelector(requestStatusName)(getState());
  if (skipRequest({ requestStatus, id: requestStatusId })) {
    return;
  }
  const markStatus = markStatusFunctionCreator(dispatch, requestStatusId, requestStatusName);

  markStatus('inProcess');

  // This is a SAFETY GUARD, not a render: it decides whether a plan has already
  // been executed and must therefore not be deleted. So it has to see the
  // patient's WHOLE history, and it must actually run.
  //
  // Both halves are load-bearing:
  //   * the full range, because the default is now RESULTS_WINDOW_DAYS -- a plan
  //     executed longer ago than that would otherwise look untouched and become
  //     deletable;
  //   * `force`, because skipRequest() returns true as soon as the patient's
  //     results have loaded once, so on the normal path (open patient -> delete)
  //     this call was being SKIPPED and returning undefined, leaving
  //     isTreatmentPlanHasResults falsy and the guard silently dead. That
  //     predates the window change; the correct call for the window change
  //     happens to close it too.
  //
  // A delete is rare and already behind a confirmation dialog, so one full-range
  // request here is the right trade against deleting executed clinical work.
  const movementResultsOutputDto = await fetchMovementsResultsRoutine({
    dispatch,
    getState,
    patientId,
    startDate: START_DATE_FOR_REQUESTS.toISOString(),
    force: true,
  });
  const isTreatmentPlanHasResults = movementResultsOutputDto?.movements.some(
    ({ treatment_plan_id }) => treatment_plan_id === treatmentPlanId,
  );
  if (isTreatmentPlanHasResults) {
    dispatch(
      notify({
        message:
          'The treatment plan that has already been executed, and cannot be deleted or modified',
        severity: 'error',
      }),
    );
    return;
  }

  try {
    await deleteTreatmentPlan(treatmentPlanId, patientId);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    markStatus('failed');
    throw e;
  }
  markStatus('succeeded');
}

export function deleteTreatmentPlanAction({
  treatmentPlan,
  patientId,
}: {
  treatmentPlan: TreatmentPlan;
  patientId: string;
}): AppThunk {
  return async (dispatch, getState) => {
    const userAnswer = await AskUser.getInstance().askUser({
      msg: `Are you sure you want to delete \"${treatmentPlan.name}\"?`,
      answers: ['Cancel', 'Delete'],
    });
    if (userAnswer === 'Cancel') {
      return;
    }
    await deleteTreatmentPlanRoutine({
      dispatch,
      getState,
      treatmentPlanId: treatmentPlan.id,
      patientId,
    });
    dispatch(fetchAssignTreatmentPlansAction({ patientId }));
  };
}

export function deleteTreatmentPlanTemplateAction({
  treatmentPlanTemplate,
}: {
  treatmentPlanTemplate: TreatmentPlanTemplate;
}): AppThunk {
  return async (dispatch, getState) => {
    const userAnswer = await AskUser.getInstance().askUser({
      msg: `Are you sure you want to delete \"${treatmentPlanTemplate.name}\"?`,
      answers: ['Cancel', 'Delete'],
    });
    if (userAnswer === 'Cancel') {
      return;
    }
    const requestStatusId = treatmentPlanTemplate.id;
    const requestStatusName: RequestStatusName = 'deleteTreatmentPlanTemplate';
    const requestStatus = getRequestStatusSelector(requestStatusName)(getState());
    if (skipRequest({ requestStatus, id: requestStatusId })) {
      return;
    }
    const markStatus = markStatusFunctionCreator(dispatch, requestStatusId, requestStatusName);
    markStatus('inProcess');
    try {
      dispatch(deleteTreatmentPlanTemplateFromStoreAction({ id: treatmentPlanTemplate.id }));
      await deleteTreatmentPlanTemplate(treatmentPlanTemplate.id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      dispatch(
        notify({
          message: `failed to delete template: ${treatmentPlanTemplate.name}`,
          severity: 'error',
        }),
      );
      dispatch(
        addTreatmentPlanTemplateAction({
          treatmentPlanTemplateDto: treatmentPlanTemplate.treatmentPlanTemplateDto,
        }),
      );
      return;
    }
    markStatus('succeeded');
  };
}

function doesTreatmentPlanHaveLocalResults(planId: string, getState: () => RootState) {
  const activitiesExecutions = getPatientActivitiesExecutionsSelector(getState());
  const hasLocalResults = activitiesExecutions?.some(
    ({ treatmentPlanId }) => treatmentPlanId === planId,
  );
  return hasLocalResults;
}

function getPlanIdToCleanBeforeAssign(getState: () => RootState) {
  const treatmentPlans = getPatientTreatmentPlansSelector(getState());
  const lastPlan = treatmentPlans?.[0];
  if (lastPlan) {
    const hasLocalResults = doesTreatmentPlanHaveLocalResults(lastPlan.id, getState);
    if (!hasLocalResults) {
      return lastPlan.id;
    }
  }
}

// Root-cause guard for the '001' / 'NA-portal' split.
//
// A legacy patient's real case row is 'NA-portal' (the UI renders it as
// '001'). If a stray literal '001' Case row also exists — or selectedCaseId
// momentarily resolves to the literal '001' — a new plan would bind to a
// DIFFERENT physical case than all the patient's history, fragmenting their
// record across two cases that look identical in the UI.
//
// This resolves the chosen caseId to the patient's actual existing case:
//   1. exact match wins (the normal case — chosen case really exists);
//   2. otherwise, if a display-equal case exists (sameCase), bind to that —
//      and when several display-equal rows exist (the 001 + NA-portal split),
//      prefer the legacy 'NA-portal' so plans land where the history lives;
//   3. otherwise pass through unchanged (the Form is responsible for having
//      created the case before dispatching).
function resolveCanonicalCaseId(caseId: string, getState: () => RootState): string {
  const patientId = getCurrentPatientIdSelector(getState());
  const caseDtos = patientId ? getPatientCaseDtosSelector(patientId)(getState()) : undefined;
  if (!caseDtos || caseDtos.length === 0) {
    return caseId;
  }
  if (caseDtos.some((c) => c.case_id === caseId)) {
    return caseId;
  }
  const displayEqual = caseDtos.filter((c) => sameCase(c.case_id, caseId));
  const legacy = displayEqual.find((c) => c.case_id === LEGACY_CASE_ID);
  const canonical = legacy ?? displayEqual[0];
  return canonical ? canonical.case_id : caseId;
}

// Edit a plan IN PLACE, for use while the patient is mid-session.
//
// Deliberately NOT folded into saveAndAssignTreatmentPlanAction: that path
// deletes the plan and POSTs a new one, which mid-session 404s the patient's
// next `GET /treatment-plan/assignment/{planId}` and terminates their session.
// The two are different operations against different endpoints, and the
// delete-and-recreate path remains correct when nobody is streaming (it is the
// only one that can insert, remove or reorder).
//
// Returns true on success so the caller can keep the editor open on failure
// rather than navigating away from unsaved work.
// 409 `plan_version_changed`: the save was made against an older version of the plan.
function isPlanVersionConflict(e: any): boolean {
  const details = e?.response?.data?.details;
  return (
    e?.response?.status === 409 &&
    typeof details === 'string' &&
    details.startsWith('plan_version_changed')
  );
}

export function updateLiveTreatmentPlanAction(
  planId: string,
  treatmentPlanForm: TreatmentPlanInput,
  originalActivities: ActivityInput[],
  updateReason?: string,
  // The plan version the form was loaded (or last merged) at. Sent as expected_version so the
  // server refuses a save made against an older plan instead of writing its stale order.
  // Undefined on a server without plan versions, which keeps the old last-write-wins save.
  expectedVersion?: number,
): AppThunk<Promise<boolean>> {
  return async (dispatch, getState) => {
    const patientId = getCurrentPatientIdSelector(getState());
    if (!patientId) {
      throw new Error('patientId is missing');
    }
    const chosenCaseId = getSelectedCaseIdSelector(getState());
    if (!chosenCaseId) {
      throw new Error('caseId is missing');
    }
    const caseId = resolveCanonicalCaseId(chosenCaseId, getState);
    const requestStatusName: RequestStatusName = 'saveAndAssignTreatmentPlan';
    cleanRequestStatusAction({ requestStatusName, id: treatmentPlanForm.id });
    const markStatus = markStatusFunctionCreator(
      dispatch,
      treatmentPlanForm.id,
      requestStatusName,
    );
    markStatus('inProcess');
    startWorkflowTimer(WORKFLOWS.TREATMENT_PLAN_SAVE);
    trackPortalEvent(PORTAL_EVENTS.TREATMENT_PLAN_SAVE_REQUESTED, {
      outcome: 'requested',
      featureArea: 'treatment_plans',
    });
    try {
      const activitiesDefinitions = {
        ...getActivityDefinitionsSelector(getState()),
        ...getCustomActivityDefinitionsByMovementIdSelector(getState()),
      };
      const userId = getUserIdSelector(getState());
      if (!userId) {
        throw new Error('userId is missing');
      }
      const patch = (
        form: TreatmentPlanInput,
        baseline: ActivityInput[],
        version: number | undefined,
      ) => {
        const treatmentPlanInputDto = toTreatmentPlanInputDto({
          treatmentPlanForm: form,
          assignorId: userId,
          activitiesDefinitions,
          caseId,
          originalActivities: baseline,
          updateReason,
          // This is the ONLY save that can reach a patient who is streaming, so it is the only
          // one that has to match what their tablet understands. See
          // LIVE_EDIT_SENDS_SLOT_IDENTITY.
          useSlotIdentity: LIVE_EDIT_SENDS_SLOT_IDENTITY,
        });
        if (version !== undefined) {
          treatmentPlanInputDto.expected_version = version;
        }
        return updateAssignedTreatmentPlan({ planId, patientId, treatmentPlanInputDto });
      };
      try {
        await patch(treatmentPlanForm, originalActivities, expectedVersion);
      } catch (e) {
        // The plan moved on while the clinician was editing. If what changed is the patient
        // moving exercises to the end (PAC-127), fold that into this save and send it once more,
        // so the save neither fails nor undoes the patient's move. Anything else — or a second
        // conflict — is a real collision and surfaces as below.
        if (!isPlanVersionConflict(e)) {
          throw e;
        }
        const definitions = getAllActivityDefinitionsSelector(getState());
        const freshDto = await fetchTreatmentPlanById({ planId, patientId });
        const merged = mergePatientDeferrals(
          originalActivities,
          treatmentPlanForm.activities,
          activitiesOfPlan(freshDto, definitions),
        );
        if (!merged) {
          throw e;
        }
        await patch(
          { ...treatmentPlanForm, activities: merged.activities },
          merged.baseline,
          freshDto.version,
        );
        if (merged.movedSlotIds.length) {
          dispatch(
            notify({
              message:
                'Saved together with the patient’s change: they moved ' +
                `${movedExerciseNames(merged.movedSlotIds, merged.activities, definitions)} ` +
                'to the end of the plan (no equipment at hand).',
              severity: 'info',
            }),
          );
        }
      }
      const treatmentPlansDto = await fetchUserTreatmentPlans({
        userId: patientId,
        startDate: START_DATE_FOR_REQUESTS,
        endDate: END_DATE_FOR_REQUESTS,
      });
      if (treatmentPlansDto) {
        dispatch(setTreatmentPlansAction({ treatmentPlansDto, patientId }));
      }
    } catch (e) {
      // The message is shown to the clinician, so a 409 has to be legible: it
      // means the edit changed the plan's SHAPE (reorder / insert / remove) or
      // someone else edited the plan since it was loaded. Both need a human
      // decision, and silently retrying either would be wrong.
      const status = e?.response?.status;
      const message =
        status === 409
          ? 'This plan changed since you opened it, or the edit moved exercises ' +
            'around. Reload the plan and try again.'
          : 'Could not update the plan. Nothing was saved.';
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(notify({ message, severity: 'error' }));
      markStatus('failed');
      trackPortalEvent(PORTAL_EVENTS.TREATMENT_PLAN_SAVE_FAILED, {
        outcome: 'failed',
        featureArea: 'treatment_plans',
      });
      completeWorkflowTimer(WORKFLOWS.TREATMENT_PLAN_SAVE, 'failed');
      return false;
    }
    markStatus('succeeded');
    trackPortalEvent(PORTAL_EVENTS.TREATMENT_PLAN_SAVE_SUCCEEDED, {
      outcome: 'success',
      featureArea: 'treatment_plans',
    });
    completeWorkflowTimer(WORKFLOWS.TREATMENT_PLAN_SAVE, 'success');
    return true;
  };
}

// The plan a save replaces could not be deleted. Two reasons are fine and the save goes on: it is
// already gone (404: a double-click's first save deleted it), or the patient has started it, so it
// now has results and stays as history (400 "already has execution results"). Anything else (the
// network, a 5xx) stops the save: assigning anyway would leave the unused old plan next to the
// new one. This used to test `e.details.contain(...)` on an error that never had `details`, so
// every failure was ignored.
export function oldPlanMayStay(e: unknown): boolean {
  const { status, details } = (e ?? {}) as { status?: number; details?: unknown };
  return (
    status === 404 ||
    (status === 400 &&
      typeof details === 'string' &&
      details.includes('already has execution results'))
  );
}

export function saveAndAssignTreatmentPlanAction(
  treatmentPlanForm: TreatmentPlanInput,
  // Step 7: the Create Treatment Plan form now owns Case ID selection
  // (between Name and Date). When the form passes one explicitly, it
  // overrides the patient-header-level selectedCaseId. The override is
  // used as-is — if the case doesn't exist on the Percix side yet, the
  // Form's onSave is responsible for creating it (via createCase) before
  // dispatching this thunk.
  caseIdOverride?: string,
  // Resolves true once the plan is assigned, false on failure — like its sibling
  // updateLiveTreatmentPlanAction — so a caller outside the form (Perci's chat-side
  // Save) can tell a saved plan from a failed one. The form ignores it, as before.
): AppThunk<Promise<boolean>> {
  return async (dispatch, getState) => {
    const patientId = getCurrentPatientIdSelector(getState());
    if (!patientId) {
      throw new Error('patientId is missing');
    }
    const chosenCaseId = caseIdOverride ?? getSelectedCaseIdSelector(getState());
    if (!chosenCaseId) {
      throw new Error('caseId is missing');
    }
    // Bind the plan to the patient's canonical case, never a stray
    // display-duplicate (the '001' / 'NA-portal' split). See helper above.
    const caseId = resolveCanonicalCaseId(chosenCaseId, getState);
    const requestStatusId = treatmentPlanForm.id;
    const requestStatusName: RequestStatusName = 'saveAndAssignTreatmentPlan'; //////
    // One save per form at a time. A double-click on Save and Assign dispatched this twice: both
    // deleted the plan being replaced (the second got a 404, swallowed below) and both assigned,
    // giving the patient the same plan twice. The status is marked synchronously below, before any
    // await, so the second call always sees the first.
    if (
      isInProcess({
        id: requestStatusId,
        requestStatus: getRequestStatusSelector(requestStatusName)(getState()),
      })
    ) {
      return false;
    }
    cleanRequestStatusAction({ requestStatusName, id: requestStatusId });
    const markStatus = markStatusFunctionCreator(dispatch, requestStatusId, requestStatusName);
    markStatus('inProcess');
    startWorkflowTimer(WORKFLOWS.TREATMENT_PLAN_SAVE);
    trackPortalEvent(PORTAL_EVENTS.TREATMENT_PLAN_SAVE_REQUESTED, {
      outcome: 'requested',
      featureArea: 'treatment_plans',
    });
    try {
      const planIdToDelete: string | undefined = getPlanIdToCleanBeforeAssign(getState);
      // Never while the patient is streaming: that plan may be the one their session runs.
      if (planIdToDelete && (await mayDeleteLatestPlanBeforeAssign(patientId))) {
        try {
          await deleteTreatmentPlan(planIdToDelete, patientId);
        } catch (e) {
          if (!oldPlanMayStay(e)) {
            throw e;
          }
        }
      }
      // Merge custom-activity versions into the definitions map so toMovementInput resolves
      // custom ids (it throws on any id absent from this map).
      const activitiesDefinitions = {
        ...getActivityDefinitionsSelector(getState()),
        ...getCustomActivityDefinitionsByMovementIdSelector(getState()),
      };
      const userId = getUserIdSelector(getState());
      if (!userId) {
        throw new Error('userId is missing');
      }
      const treatmentPlanInputDto: TreatmentPlanInputDto = toTreatmentPlanInputDto({
        treatmentPlanForm,
        assignorId: userId,
        activitiesDefinitions,
        caseId,
      });
      const treatmentPlanDto = await assignTreatmentPlan({
        patientId,
        treatmentPlanInputDto,
      });
      if (!treatmentPlanDto) {
        throw new Error();
      }
      const treatmentPlansDto = await fetchUserTreatmentPlans({
        userId: patientId,
        startDate: START_DATE_FOR_REQUESTS,
        endDate: END_DATE_FOR_REQUESTS,
      });
      if (treatmentPlansDto) {
        dispatch(setTreatmentPlansAction({ treatmentPlansDto, patientId }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      trackPortalEvent(PORTAL_EVENTS.TREATMENT_PLAN_SAVE_FAILED, {
        outcome: 'failed',
        featureArea: 'treatment_plans',
      });
      completeWorkflowTimer(WORKFLOWS.TREATMENT_PLAN_SAVE, 'failed');
      return false;
    }
    markStatus('succeeded');
    trackPortalEvent(PORTAL_EVENTS.TREATMENT_PLAN_SAVE_SUCCEEDED, {
      outcome: 'success',
      featureArea: 'treatment_plans',
    });
    completeWorkflowTimer(WORKFLOWS.TREATMENT_PLAN_SAVE, 'success');
    return true;
  };
}
